import React, { useCallback, useEffect, useMemo } from "react";
import { Button, DeleteIcon, Input, PlusIcon, Select } from "@pd/pd-ui-kit";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Modal from "~/shared/ui/Fields/Modal";
import { useDealersForSelect } from "~/entity/Dealers/hooks/useDealersForSelect";
import { useGetExpectationsForSelect } from "~/entity/Employee";
import { useGetLicenseRequestById } from "~/entity/Licenses";
import { useGetPartnersForSelect } from "~/entity/Partners/hooks/useGetPartnersForSelect";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  ILicenseRequestForm,
  ILicenseRequestPositionForm,
} from "~/pages/LicenseManagement/model/types";
import { handlePopupScroll } from "~/shared/lib/handlePopupScroll";
import { parseNumeric } from "~/shared/lib/parseNumeric";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

interface LicenseRequestModalProps {
  onSubmit: (data: ILicenseRequestForm, send: boolean) => void;
  canSelectDealer: boolean;
}

const defaultPositionRow: ILicenseRequestPositionForm = {
  position: null,
  quantity: "",
};

const getHeaderDealer = (): ILicenseRequestForm["dealer"] => {
  const id = sessionStorage.getItem("currentDealerId");
  const name = sessionStorage.getItem("currentDealerName");

  if (id && name) {
    return { value: id, label: name };
  }

  return null;
};

const buildDefaultValues = (): ILicenseRequestForm => ({
  dealer: getHeaderDealer(),
  partner: null,
  positions: [{ ...defaultPositionRow }],
});

const hasFilledPositions = (positions: ILicenseRequestPositionForm[]) =>
  positions.some(
    (row) => row.position?.value && (parseNumeric(row.quantity) ?? 0) > 0,
  );

const LicenseRequestModal = ({
  onSubmit,
  canSelectDealer,
}: LicenseRequestModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter();
  const isOpen = openModal === "CreateRequest";
  const editId = isOpen && currentId ? currentId : undefined;

  const { request: editRequest, isLoading: editRequestLoading } =
    useGetLicenseRequestById(editId);
  const isEditLoading = Boolean(editId) && editRequestLoading;

  const {
    options: expectationOptions,
    loading: positionsLoading,
    onSearch: searchText,
    onOpenChange: handlePositionsOpenChange,
  } = useGetExpectationsForSelect();

  const {
    options: dealerOptions,
    loading: dealersLoading,
    onScrollBottom: onDealersScrollBottom,
    searchDealers,
  } = useDealersForSelect();

  const {
    options: partnerOptions,
    loading: partnersLoading,
    onSearch: onPartnerSearch,
    onOpenChange: onPartnerOpenChange,
  } = useGetPartnersForSelect();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ILicenseRequestForm>({
      defaultValues: useMemo(buildDefaultValues, []),
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "positions",
  });

  const positionsValue = watch("positions");
  const dealerValue = watch("dealer");
  const partnerValue = watch("partner");

  const dealerOptionsWithSelected = useMemo(() => {
    if (!dealerValue) {
      return dealerOptions;
    }

    const exists = dealerOptions.some(
      (option) => String(option.value) === String(dealerValue.value),
    );

    if (exists) {
      return dealerOptions;
    }

    return [dealerValue, ...dealerOptions];
  }, [dealerOptions, dealerValue]);

  const partnerOptionsWithSelected = useMemo(() => {
    if (!partnerValue) {
      return partnerOptions;
    }

    const exists = partnerOptions.some(
      (option) => String(option.value) === String(partnerValue.value),
    );

    if (exists) {
      return partnerOptions;
    }

    return [partnerValue, ...partnerOptions];
  }, [partnerOptions, partnerValue]);

  const positionOptions = useMemo(() => {
    const selected =
      positionsValue?.map((item) => item.position).filter(Boolean) ?? [];

    const extra = selected.filter(
      (item) =>
        item &&
        !expectationOptions.some(
          (option) =>
            option.value === item.value || option.label === item.label,
        ),
    );

    const extras = extra.reduce<Array<{ value: string; label: string }>>(
      (acc, item) => {
        if (item) {
          acc.push({ value: item.value, label: item.label });
        }

        return acc;
      },
      [],
    );

    return [...expectationOptions, ...extras];
  }, [expectationOptions, positionsValue]);

  const canSubmit = useMemo(
    () =>
      hasFilledPositions(positionsValue ?? []) &&
      Boolean(dealerValue?.value || partnerValue?.value),
    [positionsValue, dealerValue, partnerValue],
  );

  const handleDealersPopupScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) =>
      handlePopupScroll(event, onDealersScrollBottom),
    [onDealersScrollBottom],
  );

  const handleDealersOpenChange = (open: boolean) => {
    if (open) {
      searchDealers("");
    }
  };

  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  useEffect(() => {
    if (!isOpen) {
      reset(buildDefaultValues());
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!editId || !editRequest) {
      return;
    }

    const positions = (editRequest.items || []).map((item) => {
      const firstPosition = item.positions?.[0];

      return {
        position:
          item.instanceId != null
            ? {
                value: String(item.instanceId),
                label: firstPosition
                  ? `${firstPosition.code}. ${firstPosition.name}`
                  : String(item.instanceId),
              }
            : null,
        quantity: item.quantity != null ? String(item.quantity) : "",
      };
    });

    let dealer: ILicenseRequestForm["dealer"] = getHeaderDealer();

    if (editRequest.organizationId) {
      dealer = null;
    } else if (editRequest.dealerId) {
      dealer = {
        value: String(editRequest.dealerId),
        label: editRequest.dealerName || String(editRequest.dealerId),
      };
    }

    reset({
      dealer,
      partner: editRequest.organizationId
        ? {
            value: String(editRequest.organizationId),
            label:
              editRequest.organizationName ||
              String(editRequest.organizationId),
          }
        : null,
      positions: positions.length ? positions : [{ ...defaultPositionRow }],
    });
  }, [editId, editRequest, reset]);

  const submitWithStatus = (send: boolean) =>
    handleSubmit((data) => {
      if (
        !hasFilledPositions(data.positions) ||
        (!data.dealer?.value && !data.partner?.value)
      ) {
        return;
      }

      onSubmit(data, send);
      closeModal();
    });

  return (
    <Modal
      isOpen={isOpen}
      title={t("licenseManagement.modal.title")}
      actionClose={closeModal}
      w={948}
    >
      <div className={s.modalForm}>
        <div className={s.modalRow}>
          <div className={s.modalField}>
            <span className={s.modalLabel}>
              {t("licenseManagement.modal.dealer")}
            </span>
            <Controller
              name="dealer"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelInValue
                  allowClear={canSelectDealer}
                  showSearch={canSelectDealer}
                  filterOption={false}
                  value={value}
                  onChange={
                    canSelectDealer
                      ? (option) => {
                          onChange(option);
                          if (option?.value) {
                            setValue("partner", null);
                          }
                        }
                      : undefined
                  }
                  options={dealerOptionsWithSelected}
                  loading={dealersLoading || isEditLoading}
                  onSearch={canSelectDealer ? searchDealers : undefined}
                  onOpenChange={
                    canSelectDealer ? handleDealersOpenChange : undefined
                  }
                  onPopupScroll={
                    canSelectDealer ? handleDealersPopupScroll : undefined
                  }
                  disabled={!canSelectDealer || isEditLoading}
                  placeholder={t("licenseManagement.modal.notSelected")}
                />
              )}
            />
          </div>

          <div className={s.modalField}>
            <span className={s.modalLabel}>
              {t("licenseManagement.modal.partner")}
            </span>
            <Controller
              name="partner"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelInValue
                  allowClear={canSelectDealer}
                  showSearch={canSelectDealer}
                  filterOption={false}
                  value={value}
                  onChange={
                    canSelectDealer
                      ? (option) => {
                          onChange(option);
                          if (option?.value) {
                            setValue("dealer", null);
                          }
                        }
                      : undefined
                  }
                  options={partnerOptionsWithSelected}
                  loading={partnersLoading || isEditLoading}
                  onSearch={canSelectDealer ? onPartnerSearch : undefined}
                  onOpenChange={
                    canSelectDealer ? onPartnerOpenChange : undefined
                  }
                  disabled={!canSelectDealer || isEditLoading}
                  placeholder={t("licenseManagement.modal.notSelected")}
                />
              )}
            />
          </div>
        </div>

        <div className={s.modalRows}>
          {fields.map((field, index) => (
            <div key={field.id} className={s.modalRow}>
              <div className={s.modalField}>
                <span className={s.modalLabel}>
                  {t("licenseManagement.modal.position")}
                </span>
                <Controller
                  name={`positions.${index}.position`}
                  control={control}
                  render={({ onChange, value }) => (
                    <Select
                      labelInValue
                      allowClear
                      showSearch
                      filterOption={false}
                      value={value}
                      onChange={onChange}
                      options={positionOptions}
                      loading={positionsLoading || isEditLoading}
                      onSearch={searchText}
                      onOpenChange={handlePositionsOpenChange}
                      placeholder={t("licenseManagement.modal.notSelected")}
                    />
                  )}
                />
              </div>

              <div className={s.modalFieldFixed}>
                <span className={s.modalLabel}>
                  {t("licenseManagement.modal.quantity")}
                </span>
                <Controller
                  name={`positions.${index}.quantity`}
                  control={control}
                  render={({ onChange, value, onBlur }) => (
                    <Input
                      value={value}
                      onChange={(event) =>
                        onChange(event.target.value.replace(/[^\d]/g, ""))
                      }
                      onBlur={onBlur}
                      disabled={isEditLoading}
                      placeholder="1"
                    />
                  )}
                />
              </div>

              {fields.length >= 2 && (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  htmlType="button"
                  icon={<DeleteIcon size={16} />}
                  aria-label={t("ui.delete")}
                  title={t("ui.delete")}
                  onClick={() => remove(index)}
                />
              )}
            </div>
          ))}

          <Button
            variant="text"
            color="primary"
            fontSize="12"
            size="small"
            htmlType="button"
            icon={<PlusIcon size={16} />}
            onClick={() => append({ ...defaultPositionRow })}
          >
            {t("licenseManagement.modal.addPosition")}
          </Button>
        </div>

        <div className={s.modalFooter}>
          <div className={s.divider} />
          <div className={s.modalButtons}>
            <Button
              bold
              color="secondary"
              fontSize="14"
              size="medium"
              radius={5}
              onClick={closeModal}
            >
              {t("licenseManagement.modal.close")}
            </Button>
            <Button
              bold
              color="secondary"
              fontSize="14"
              size="medium"
              radius={5}
              disabled={!canSubmit || isEditLoading}
              onClick={submitWithStatus(false)}
            >
              {t("licenseManagement.modal.save")}
            </Button>
            <Button
              bold
              color="primary"
              fontSize="14"
              size="medium"
              radius={5}
              disabled={!canSubmit || isEditLoading}
              onClick={submitWithStatus(true)}
            >
              {t("licenseManagement.modal.send")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseRequestModal;
